/// <reference path="../../../../global.d.ts" />

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DOCUMENT,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslatedDatepickerModule } from '@jfw/angular/shared/i18n';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { CustomTooltipModule } from '@jfw/angular/ecommerce/common-components/custom-tooltip';
import { StickyStackTopDirective } from '@jfw/angular/shared/utils-angular';
import { getStyleTypeFromCode, PRODUCT_PRIORITY } from '@jfw/business-logic';
import {
  EzTuxEnvironment,
  EzTuxParentApplication,
  OrderListItem,
  OrderListItemDetail,
  StyleType,
} from '@jfw/types';
import { Subject, Subscription } from 'rxjs';
import { WholesaleOrderListViewService } from '../service/order-list-view.service';
import { WholesaleTrackingInfoModalComponent } from '../tracking-info-modal/tracking-info-modal.component';
// moved to jfw-library/global.d.ts
// declare global {
//   interface Window {
//     isCSRMode: () => string;
//   }
// }

@Component({
  selector: 'jfw-wholesale-order-list-view',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    TranslatedDatepickerModule,
    MatCheckboxModule,
    MatTableModule,
    MatProgressSpinnerModule,
    CommonModule,
    MatIconModule,
    CustomTooltipModule,
    MatDialogModule,
    StickyStackTopDirective,
  ],
  templateUrl: './order-list-view.component.html',
  styleUrl: './order-list-view.component.scss',
})
export class WholesaleOrderListViewComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
  private router = inject(Router);
  // New input property for direct orders
  @Input() receivedOrderList: OrderListItem[] | undefined = [];

  // Signal to hold the currently displayed order list
  localOrderList = signal<OrderListItem[] | undefined>(undefined);

  // EXT-5972: opt-in sticky header pinning. Left unbound (default ''), the
  // header row keeps its previous non-sticky behavior for consumers other
  // than the Search page (Data Pods, dealer-portal) -- see
  // StickyStackTopDirective's own doc comment.
  public readonly stickyHeaderStackAbove = input<string>('');

  protected readonly SORT_KEYS = [
    'eventDate',
    'orderNumber',
    'customer',
    'eventDescription',
    'orderType',
    'orderStatus',
  ] as const;
  protected readonly sortKey = signal<(typeof this.SORT_KEYS)[number]>('eventDate');
  protected readonly sortDirection = signal<1 | -1>(1);
  protected readonly limit = signal(50);

  // EXT-6037: lets a consumer (e.g. the This Week/Next Week data pods) pick a
  // different initial sort than the shared default of eventDate, without
  // touching SORT_KEYS/sortBy() -- the user can still re-sort by any column
  // afterward exactly as before. Left unbound, this stays 'eventDate', so the
  // eztux/KMS embed (<ezt-order-list-view>, which never binds it) is
  // unaffected -- same pattern as filterTerm/stickyHeaderStackAbove above.
  @Input() defaultSortKey: 'eventDate' | 'orderNumber' = 'eventDate';

  // EXT-6424: opt-in compact layout. While this list is narrower than
  // compactBelow (px), Edit/Track/Receipt become one Actions column and the
  // eight garment columns become one Garments column, so the table fits a
  // narrower screen. Left unbound (0) it never switches, so the eztux/KMS
  // embed and Platform Portal keep the full column set -- same pattern as
  // defaultSortKey/filterTerm above. Only applies to the new design.
  public readonly compactBelow = input<number>(0);
  private readonly containerWidth = signal<number>(Number.POSITIVE_INFINITY);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private resizeObserver: ResizeObserver | undefined;

  get compact(): boolean {
    const below = this.compactBelow();
    return this.showNewDesign && below > 0 && this.containerWidth() < below;
  }

  // EXT-6023: in-results filter term, owned by the parent order-search
  // component (whose search-form row hosts the actual input) and passed down
  // here so the grid's own rows narrow to match. Matches Customer/Event
  // columns case-insensitively. No MatTableDataSource exists here (unlike the
  // Event tab), so this is a plain computed() filter over the same
  // signal/computed pipeline the existing sort/pagination already use. A
  // plain one-way input (not a two-way model()) since this component never
  // writes it -- the eztux/KMS embed (<ezt-order-list-view>) never binds it
  // at all, so it stays at its default '' there, leaving that frozen embed's
  // behavior unchanged.
  public readonly filterTerm = input<string>('');

  // EXT-6596: the account loaded on the host page. Platform Portal's Event
  // Planner sets it so a member's order on another account shows that
  // account by its number; everyone else leaves it '' and sees no tag.
  public readonly currentAccountNumber = input<string>('');

  protected otherAccountOf(order: OrderListItem): string | null {
    const current = this.currentAccountNumber().trim().toUpperCase();
    const account = order.orderAccountNumber?.trim().toUpperCase() ?? '';
    return current && account && account !== current ? account : null;
  }

  // The # column widens for the tag only when some order actually needs one.
  // Checked over the whole list, not the visible page, so the column doesn't
  // change width as more rows load or the filter narrows.
  protected readonly showsOtherAccounts = computed(
    () =>
      this.localOrderList()?.some((order) => !!this.otherAccountOf(order)) ??
      false,
  );

  private sortValueOf(order: OrderListItem, key: (typeof this.SORT_KEYS)[number]) {
    switch (key) {
      case 'eventDate':
        return order.eventDate ?? '';
      case 'orderNumber':
        return order.orderNumber ?? 0;
      case 'customer':
        return order.fullName ?? '';
      case 'eventDescription':
        return order.eventDescription ?? '';
      case 'orderType':
        return order.orderType ?? '';
      case 'orderStatus':
        return this.getOrderStatus(order);
    }
  }

  // EXT-6023: filtered view over localOrderList, matched against Customer
  // (fullName) and Event (eventDescription) case-insensitively. Sits ahead of
  // sortedOrderList so filtering and sorting compose; hasMore/endLabel below
  // key off this (not localOrderList directly) so "Load 50 more" stops
  // showing once the filtered set is fully revealed, even though it's still
  // below the unfiltered total.
  protected readonly filteredOrderList = computed(() => {
    const list = this.localOrderList();
    if (!list) return undefined;
    const term = this.filterTerm().trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      (order) =>
        (order.fullName ?? '').toLowerCase().includes(term) ||
        (order.eventDescription ?? '').toLowerCase().includes(term),
    );
  });

  // EXT-5972: sorted view over localOrderList -- default is event date, then
  // order number ascending (so # reads sequentially within a date), per AC.
  protected readonly sortedOrderList = computed(() => {
    const list = this.filteredOrderList();
    if (!list) return undefined;
    const key = this.sortKey();
    const dir = this.sortDirection();
    return [...list].sort((a, b) => {
      const av = this.sortValueOf(a, key);
      const bv = this.sortValueOf(b, key);
      let cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      if (cmp === 0 && key === 'eventDate') {
        cmp = (a.orderNumber ?? 0) - (b.orderNumber ?? 0);
      }
      return cmp * dir;
    });
  });

  protected readonly visibleOrderList = computed(() =>
    this.sortedOrderList()?.slice(0, this.limit()),
  );

  protected readonly hasMore = computed(
    () => this.limit() < (this.filteredOrderList()?.length ?? 0),
  );

  protected readonly endLabel = computed(() => {
    const total = this.filteredOrderList()?.length ?? 0;
    const shown = Math.min(this.limit(), total);
    return shown >= total
      ? `End of results · ${total} rows`
      : `Showing ${shown} of ${total}`;
  });

  protected sortBy(key: (typeof this.SORT_KEYS)[number]): void {
    if (this.sortKey() === key) {
      this.sortDirection.update((dir) => (dir === 1 ? -1 : 1) as 1 | -1);
    } else {
      this.sortKey.set(key);
      this.sortDirection.set(1);
    }
  }

  protected loadMore(): void {
    this.limit.update((n) => n + 50);
  }

  @Input() eztux_base_url: string = 'https://ezdevbo-1.jimsfw.com';

  @Input() order_search_api_server_url: string = '';

  // Subject for handling the direct input changes
  private directOrderSubject = new Subject<OrderListItem[] | undefined>();
  private btid: string | null = '';
  private dialog = inject(MatDialog);
  loaded = signal(false);
  error = signal('');
  form = new FormGroup({
    accountNumber: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: Validators.required,
    }),
    fromDate: new FormControl<Date | null>(null, {
      nonNullable: true,
      validators: Validators.required,
    }),
    toDate: new FormControl<Date | null>(null, {
      nonNullable: true,
      validators: Validators.required,
    }),
  });

  initialLoad: boolean = true;
  csrMode: boolean = false;

  loadingOrderId: number | undefined;
  loadingOrderNumber: number | undefined;
  loadingEventId: string | undefined;

  @Input()
  set resetloading(value: string | boolean) {
    if (value !== undefined && (value === 'true' || value === true)) {
      this.initialLoad = value === true || value === 'true';
    }
  }

  @Input() parent_application: EzTuxParentApplication;
  @Input() environment: EzTuxEnvironment;
  @Input() newSite = false;

  private document = inject(DOCUMENT);
  private orderListSubscription: Subscription | undefined;

  orderListWrapper$ = this.orderListViewService.orderListWrapper$;

  constructor(private orderListViewService: WholesaleOrderListViewService) {}

  // EXT-6188: a producer can record the default sort it wants for one
  // specific list (see WholesaleOrderListViewService.setDefaultSortKeyForList).
  // That's how the KMS/EZTux embed gets per-pod sorting, since the KMS page
  // wires <ezt-pods> to <ezt-order-list-view> with script we don't own and so
  // can never bind defaultSortKey. An explicitly bound defaultSortKey stays
  // the fallback, which is what jfw-platform-portal/dealer-portal use.
  private resolveDefaultSortKey(
    list: OrderListItem[] | undefined,
  ): 'eventDate' | 'orderNumber' {
    return (
      this.orderListViewService.getDefaultSortKeyForList(list) ??
      this.defaultSortKey
    );
  }

  ngOnInit(): void {
    this.sortKey.set(this.resolveDefaultSortKey(this.receivedOrderList));
    this.setCSRMode();

    if (this.receivedOrderList && this.receivedOrderList.length > 0) {
      this.localOrderList.set(this.receivedOrderList);
      this.limit.set(50);
      this.loaded.set(true);
    }

    this.orderListSubscription =
      this.orderListViewService.orderListWrapper$.subscribe((wrapper) => {
        if (wrapper.status === 'PENDING') {
          // Only clear loaded status if we aren't displaying local orders
          if (!this.receivedOrderList || this.receivedOrderList.length === 0) {
            this.loaded.set(false);
            this.error.set('');
          }
        } else if (wrapper.status === 'READY') {
          if (
            Array.isArray(wrapper.orderList) &&
            wrapper.orderList.every((item) => 'internalOrderNumber' in item)
          ) {
            // Only update localOrderList if receivedOrderList wasn't provided directly as an input
            if (
              !this.receivedOrderList ||
              this.receivedOrderList.length === 0
            ) {
              this.localOrderList.set(wrapper.orderList as OrderListItem[]);
              this.limit.set(50);
              this.loaded.set(true);
              this.error.set('');
            }
          } else {
            if (
              !this.receivedOrderList ||
              this.receivedOrderList.length === 0
            ) {
              this.localOrderList.set(undefined);
              this.loaded.set(true);
              this.error.set('');
            }
          }
        } else if (wrapper.status === 'ERROR') {
          if (!this.receivedOrderList || this.receivedOrderList.length === 0) {
            this.loaded.set(true);
          }
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['receivedOrderList']) {
      const newList = changes['receivedOrderList'].currentValue;
      if (newList !== undefined && newList.length > 0) {
        // EXT-6037: this component instance is reused across pod switches
        // (the @if in data-pods'/dashboard's own templates never actually
        // tears it down between a clear-then-repopulate pair fired
        // synchronously by setActivePod() -- Angular's change detection only
        // observes the final signal value) -- so a fresh, non-empty
        // receivedOrderList is the real "a (possibly different) pod's data
        // just arrived" signal, not just ngOnInit, and is where the sort
        // needs to be re-applied to avoid carrying over a manual re-sort
        // from whichever pod was open before this one.
        // EXT-6188: prefer a per-list default recorded by whoever produced
        // this list (the data pods) over this component's own input.
        this.sortKey.set(this.resolveDefaultSortKey(newList));
        this.sortDirection.set(1);
        this.localOrderList.set(newList);
        this.limit.set(50);
        this.loaded.set(true);
      } else {
        // Fallback to wrapper if input is cleared
        const currentWrapper = this.orderListViewService.getOrderListWrapper();
        if (currentWrapper && currentWrapper.status === 'READY') {
          this.localOrderList.set(currentWrapper.orderList as OrderListItem[]);
        } else {
          this.localOrderList.set(undefined);
        }
      }
    }
  }

  // EXT-6424: measures .container, not the host -- the host element is
  // inline (and stays so for the eztux/KMS embed), so it has no width of its
  // own to observe.
  ngAfterViewInit(): void {
    const container = this.host.nativeElement.firstElementChild;
    if (
      this.compactBelow() > 0 &&
      container &&
      typeof ResizeObserver !== 'undefined'
    ) {
      this.resizeObserver = new ResizeObserver(([entry]) =>
        this.containerWidth.set(entry.contentRect.width),
      );
      this.resizeObserver.observe(container);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.orderListSubscription) {
      this.orderListSubscription.unsubscribe();
    }
  }

  setCSRMode() {
    /// If the parent application is dealerportal, set csrMode to false.
    if (this.parent_application === 'dealerportal') {
      this.csrMode = false;
      // console.warn('CSR Mode is disabled');
      return;
    }

    /// If the parent application is jfwplatformportal, set csrMode to true.
    if (this.parent_application === 'jfwplatformportal') {
      this.csrMode = true;
      // console.warn('CSR Mode is enabled');
      return;
    }

    // This must be eztuxportal, so we check the window.isCSRMode function.
    try {
      const csrModeString = window.isCSRMode();

      if (csrModeString === 'weWxer!@y2dfwdNewz@5RtS-!;D#2912hdh') {
        this.csrMode = true;
        // console.warn('CSR Mode is enabled');
      } else {
        this.csrMode = false;
        // console.warn('CSR Mode is disabled');
      }
    } catch (e) {
      // console.error('Error setting CSR mode:', e);
      this.csrMode = false;
      // console.warn('CSR Mode is disabled');
    }
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   // Check if inputData property changed and is not null
  //   console.log('OrderEntry ngOnChanges called with changes:', changes);
  //   if (
  //     !this.initialLoad &&
  //     changes.receivedOrderList &&
  //     changes.receivedOrderList.currentValue !== null &&
  //     changes.receivedOrderList.currentValue !== undefined
  //   ) {
  //     // Push the input data into the subject
  //     console.log(
  //       'directOrder Received in ngOnChanges',
  //       this.receivedOrderList,
  //     );
  //     this.directOrderSubject.next(this.receivedOrderList);
  //   } else {
  //     // this.loading.set(false);
  //     // this.loaded.set(true);
  //     this.directOrderSubject.next(undefined);
  //     this.initialLoad = false;
  //   }
  // }

  // Handle direct order input changes.
  // private orders$ = this.directOrderSubject.pipe(
  //   distinctUntilChanged(),
  //   tap((result) => {
  //     console.log('orders found', result);
  //     if (result === undefined) {
  //       this.loaded.set(false);
  //       return;
  //     }
  //     this.loaded.set(true);
  //     // this.loading.set(false);
  //     // this.loaded.set(true);
  //   }),
  //   catchError((error) => {
  //     console.error('error searching orders', error);
  //     this.loaded.set(true);
  //     this.error.set('Error searching orders');
  //     return of([]);
  //   }),
  // );

  getCoatInfo(details: OrderListItemDetail[]): [string, string] {
    const coat = details.find((d) => d.orderProduct === 'CT');
    if (!coat) {
      return ['', ''];
    } else {
      return [
        `${coat.orderStyle} ${coat.orderOption1}${coat.orderOption2}`,
        coat.styleDescription,
      ];
    }
  }

  getPantInfo(details: OrderListItemDetail[]): [string, string] {
    const pant = details.find((d) => d.orderProduct === 'PT');
    if (!pant) {
      return ['', ''];
    } else {
      return [
        `${pant.orderStyle} ${pant.orderOption1} ${pant.orderOption2}`,
        pant.styleDescription,
      ];
    }
  }

  getShirtInfo(details: OrderListItemDetail[]): [string, string] {
    const shirt = details.find((d) => d.orderProduct === 'SR');
    if (!shirt) {
      return ['', ''];
    } else {
      return [
        `${shirt.orderStyle} ${shirt.orderOption1} ${shirt.orderOption2}`,
        shirt.styleDescription,
      ];
    }
  }

  getTieInfo(details: OrderListItemDetail[]): [string, string] {
    const tie = details.find((d) => d.orderProduct === 'TI');
    if (!tie) {
      return ['', ''];
    } else {
      return [
        `${tie.orderStyle} ${tie.orderOption1} ${tie.orderOption2}`,
        tie.styleDescription,
      ];
    }
  }

  getVestInfo(details: OrderListItemDetail[]): [string, string] {
    const vest = details.find((d) => d.orderProduct === 'VS');
    if (!vest) {
      return ['', ''];
    } else {
      return [
        `${vest.orderStyle} ${vest.orderOption1} ${vest.orderOption2}`,
        vest.styleDescription,
      ];
    }
  }

  getShoeInfo(details: OrderListItemDetail[]): [string, string] {
    const shoe = details.find((d) => d.orderProduct === 'SO');
    if (!shoe) {
      return ['', ''];
    } else {
      return [
        `${shoe.orderStyle} ${shoe.orderOption1}${shoe.orderOption2}`,
        shoe.styleDescription,
      ];
    }
  }

  // EXT-6424: the compact Garments column -- every garment the order has,
  // labelled, in the same order as the full layout's columns.
  protected garmentSummary(
    details: OrderListItemDetail[],
  ): { label: string; style: string; size: string }[] {
    const garments: [string, string][] = [
      ['Coat', this.getCoatInfo(details)[0]],
      ['Pant', this.getPantInfo(details)[0]],
      ['Shirt', this.getShirtInfo(details)[0]],
      ['Tie', this.getTieInfo(details)[0]],
      ['Vest', this.getVestInfo(details)[0]],
      ['Shoe', this.getShoeInfo(details)[0]],
      ['Pkt', this.getPocketInfo(details)[0]],
      ['Jewl', this.getJewelryInfo(details)[0]],
    ];
    return garments
      .filter(([, raw]) => raw.trim())
      .map(([label, raw]) => ({ label, ...this.splitStyleSize(raw) }));
  }

  getPocketInfo(details: OrderListItemDetail[]): [string, string] {
    const pocket = details.find((d) => d.orderProduct === 'PK');
    if (!pocket) {
      return ['', ''];
    } else {
      return [`${pocket.orderStyle}`, pocket.styleDescription];
    }
  }

  getJewelryInfo(details: OrderListItemDetail[]): [string, string] {
    const jewelry = details.find((d) => d.orderProduct === 'JW');
    if (!jewelry) {
      return ['', ''];
    } else {
      return [`${jewelry.orderStyle}`, jewelry.styleDescription];
    }
  }

  // Allowlist (default-deny), matching the pattern already established by
  // redirectToReceipt() above — an unrecognized or unset parent_application
  // (including the KMS/eztux embed) always falls through to the legacy design.
  get showNewDesign(): boolean {
    return (
      this.parent_application === 'jfwplatformportal' ||
      (this.parent_application === 'dealerportal' && this.newSite)
    );
  }

  // EXT-6023: splits `text` into segments so the template can highlight the
  // portion(s) matching the active filter term (case-insensitive) without
  // resorting to [innerHTML]/sanitization.
  protected highlightSegments(
    text: string | null | undefined,
    term: string,
  ): { text: string; match: boolean }[] {
    const value = text ?? '';
    const needle = term.trim();
    if (!needle) return [{ text: value, match: false }];
    const lowerValue = value.toLowerCase();
    const lowerNeedle = needle.toLowerCase();
    const segments: { text: string; match: boolean }[] = [];
    let i = 0;
    while (i < value.length) {
      const idx = lowerValue.indexOf(lowerNeedle, i);
      if (idx === -1) {
        segments.push({ text: value.slice(i), match: false });
        break;
      }
      if (idx > i) segments.push({ text: value.slice(i, idx), match: false });
      segments.push({ text: value.slice(idx, idx + needle.length), match: true });
      i = idx + needle.length;
    }
    return segments;
  }

  splitStyleSize(raw: string): { style: string; size: string } {
    const parts = raw.trim().split(' ').filter(Boolean);
    return { style: parts[0] ?? '', size: parts.slice(1).join(' ') };
  }

  @Output() editClicked = new EventEmitter<OrderListItem>();
  @Input() interceptEditClick = false;

  redirectToEdit(details: OrderListItem): void {
    // Angular Elements automatically subscribes to all outputs, causing 'observed' to be true in the CDN build.
    // We use an explicit 'interceptEditClick' flag to intercept the routing.
    if (this.interceptEditClick) {
      this.editClicked.emit(details);
      return;
    }

    if (this.parent_application === 'jfwplatformportal') {
      // EXT-6596: the order's own account goes too. Order numbers repeat
      // across accounts, so Order Entry switches to it before loading.
      this.router.navigate(['/wholesale/order-entry'], {
        state: {
          eventProductionWeek: details.eventProductionWeek,
          orderNumber: details.orderNumber,
          accountNumber: details.orderAccountNumber,
        },
      });
      return;
    } else if (this.parent_application === 'dealerportal') {
      this.router.navigate(['/order-entry'], {
        state: {
          eventProductionWeek: details.eventProductionWeek,
          orderNumber: details.orderNumber,
        },
      });
      return;
    }
    this.loadingOrderId = details.internalOrderNumber;

    const url = `${this.eztux_base_url}/memberV2/OrderEntryFromOmni.aspx?ACTNUM=${details.orderAccountNumber}&INTNUM=${details.internalOrderNumber}`;
    // window.open(url, '_blank', 'noopener');
    window.location.href = url;
  }

  // EXT-6551: opens an event owner's potential order in Dealer Portal's
  // Order Entry for the dealer to size and save under its own account, which
  // takes the order over. Only Dealer Portal marks orders claimable.
  claimOrder(details: OrderListItem): void {
    this.router.navigate(['/order-entry'], {
      state: { claimInternalOrderNumber: details.internalOrderNumber },
    });
  }

  redirectToReceipt(details: OrderListItem): void {
    if (this.parent_application === 'jfwplatformportal') {
      this.router.navigate(['/wholesale/receipt-viewer'], {
        state: { internalOrderNumber: details.internalOrderNumber },
        queryParams: { internalOrderNumber: details.internalOrderNumber },
      });
      return;
    } else if (this.parent_application === 'dealerportal' && this.newSite) {
      this.router.navigate(['/receipt-viewer'], {
        state: { internalOrderNumber: details.internalOrderNumber },
        queryParams: { internalOrderNumber: details.internalOrderNumber },
      });
      return;
    }

    const queryString = this.document.location.search;
    const urlParams = new URLSearchParams(queryString);
    const btid = urlParams.get('btid');
    console.log('btid:', btid);
    if (btid) {
      this.btid = btid;
      const url = `${this.eztux_base_url}/MemberV2/ReceiptPopup.aspx?INTNUM=${details.internalOrderNumber}&btid=${this.btid}`;
      window.open(url, '', 'noopener');
    }
    console.log('btid:', btid);
  }

  redirectToEventManager(details: OrderListItem): void {
    const queryString = this.document.location.search;
    const urlParams = new URLSearchParams(queryString);
    const btid = urlParams.get('btid');
    console.log('btid:', btid);
    if (this.parent_application === 'jfwplatformportal') {
      this.router.navigate(['/wholesale/event-planner'], {
        queryParams: { id: details.nationalEventId },
      });
      return;
    } else if (this.parent_application === 'dealerportal') {
      this.router.navigate(['/event-planner'], {
        queryParams: { id: details.nationalEventId },
      });
      return;
    }
    if (btid) {
      this.loadingEventId = details.nationalEventId;
      this.loadingOrderNumber = details.internalOrderNumber;
      this.btid = btid;
      const url = `${this.eztux_base_url}/memberv2/EventEntry.aspx?btid=${this.btid}&NATEVID=${details.nationalEventId}`;
      // window.open(url, '', 'noopener');
      window.location.href = url;
    }
    console.log('btid:', btid);
  }

  hasReplacement(order: OrderListItem): boolean {
    return order.details?.some((item) => item.replacementCode !== '') ?? false;
  }

  hasDirectDelivery(order: OrderListItem): boolean {
    return order.shipToCode === 3;
  }

  getReplacementToolTip(order: OrderListItem): string {
    let toolTipIterator: Array<Array<string>> = [];
    order.details?.forEach((detail) => {
      const lineItem = [];
      if (detail.replacementCode || detail.replacementCode !== '') {
        lineItem.push(
          getStyleTypeFromCode(detail.orderProduct) ?? detail.orderProduct,
        );
        /**
         * Shirts only have option 1 and 2 no 3, Pants shouldn't display option 3.
         */
        if (detail.orderProduct === 'SR' || detail.orderProduct === 'PT') {
          lineItem.push(detail.orderStyle);
          lineItem.push(detail.orderOption1);
          lineItem.push(detail.orderOption2);
        } else {
          lineItem.push(detail.orderStyle);
          lineItem.push(detail.orderOption1 + detail.orderOption2);
          if (detail.orderOption3) lineItem.push(detail.orderOption3);
        }
        toolTipIterator.push(lineItem);
      }
    });
    toolTipIterator = toolTipIterator.sort((a, b) => {
      const aIndex = PRODUCT_PRIORITY.indexOf(a[0] as StyleType);
      const bIndex = PRODUCT_PRIORITY.indexOf(b[0] as StyleType);
      return aIndex - bIndex;
    });
    let grid = `
      <div style="
        display: grid;
        grid-template-columns:
        repeat(4, auto); gap: 4px;
      ">
        ${toolTipIterator
          .map((row) => {
            return (
              row.map((rowItem) => `<div>${rowItem}</div>`).join('') +
              '<div></div>'.repeat(4 - row.length)
            );
          })
          .join('')}
      </div>
    `;
    return grid;
  }

  getDirectDelieveryToolTip(order: OrderListItem): string {
    let address = '';
    const {
      firstName,
      lastName,
      shipToAddress1,
      shipToAddress2,
      shipToCity,
      shipToState,
      shipToZip,
    } = order;
    if (firstName && lastName) {
      address += `${firstName} ${lastName} <br/>`;
    }
    if (shipToAddress1) {
      address += shipToAddress1 + '<br/>';
    }
    if (shipToAddress2) {
      address += shipToAddress2 + '<br/>';
    }
    if (shipToCity && shipToState && shipToZip) {
      address += `${shipToCity}, ${shipToState}, ${shipToZip}`;
    }
    return address;
  }
  getShipToDiffActToolTip(order: OrderListItem): string {
    const {
      shipToAccountNumber,
      shipToAddress1,
      shipToName,
      shipToCity,
      shipToState,
      shipToZip,
    } = order;
    let address = '';
    if (shipToAccountNumber) {
      address += `${shipToAccountNumber}` + '<br/>';
    }
    if (shipToName) {
      address += shipToName + '<br/>';
    }
    if (shipToAddress1) {
      address += shipToAddress1 + '<br/>';
    }
    if (shipToCity && shipToState && shipToZip) {
      address += `${shipToCity}, ${shipToState}, ${shipToZip}`;
    }
    return address;
  }

  getOrderStatusToolTip(order: OrderListItem): string {
    let notCheckedInItems: Array<Array<string>> = [];
    let checkedInItems: Array<Array<string>> = [];
    order.details?.forEach((detail) => {
      const nonCheckedItem: string[] = [];
      const checkedItem: string[] = [];
      if (detail.outstanding) {
        nonCheckedItem.push(
          getStyleTypeFromCode(detail.orderProduct) ?? detail.orderProduct,
        );
        nonCheckedItem.push(detail.orderStyle);
        nonCheckedItem.push(
          String(detail.barcode === 0 ? '' : detail.barcode) ?? '',
        );
        notCheckedInItems.push(nonCheckedItem);
      }
      if (detail.orderStatus === 'C') {
        checkedItem.push(
          getStyleTypeFromCode(detail.orderProduct) ?? detail.orderProduct,
        );
        checkedItem.push(detail.orderStyle);
        checkedItem.push(String(this.formatDateString(detail.changeTime)));
        checkedInItems.push(checkedItem);
      }
    });
    notCheckedInItems = notCheckedInItems.sort((a, b) => {
      const aIndex = PRODUCT_PRIORITY.indexOf(a[0] as StyleType);
      const bIndex = PRODUCT_PRIORITY.indexOf(b[0] as StyleType);
      return aIndex - bIndex;
    });
    checkedInItems = checkedInItems.sort((a, b) => {
      const aIndex = PRODUCT_PRIORITY.indexOf(a[0] as StyleType);
      const bIndex = PRODUCT_PRIORITY.indexOf(b[0] as StyleType);
      return aIndex - bIndex;
    });
    let notCheckedGrid = ``;
    let checkedInGrid = ``;
    if (notCheckedInItems.length) {
      notCheckedGrid = `
        <div style="color: var(--status-danger, red);">
          <div>Not Checked In</div>
          <div style="
            display: grid;
            grid-template-columns:
            repeat(4, auto); gap: 4px;
          ">

            ${notCheckedInItems
              .map((row) => {
                return (
                  row.map((rowItem) => `<div>${rowItem}</div>`).join('') +
                  '<div></div>'.repeat(4 - row.length)
                );
              })
              .join('')}
          </div>
        </div>
      `;
    }
    if (checkedInItems.length) {
      checkedInGrid = `
        <div>
          <div>Checked In</div>
            <div style="
              display: grid;
              grid-template-columns:
              repeat(4, auto); gap: 4px;
            ">

              ${checkedInItems
                .map((row) => {
                  return (
                    row.map((rowItem) => `<div>${rowItem}</div>`).join('') +
                    '<div></div>'.repeat(4 - row.length)
                  );
                })
                .join('')}
            </div>
        </div>
      `;
    }

    return notCheckedGrid + checkedInGrid;
  }

  openModal(order: OrderListItem): void {
    this.dialog.open(WholesaleTrackingInfoModalComponent, {
      data: {
        order,
        apiUrl: this.order_search_api_server_url,
        csrMode: this.csrMode,
      },
      // ...modalSettings,
      width: '45%',
      minWidth: '30%',
      maxHeight: '100vh',
    });
  }

  setReceiptClass(order: OrderListItem): string {
    const balance = order.receiptBalance;
    const receiptNumber = order.receiptInternalOrderNumber;
    if (receiptNumber === -1) {
      return 'no-receipt';
    }
    if (balance > 0) {
      return 'unpaid';
    }
    if (balance === 0) {
      return 'paid';
    }
    return '';
  }

  // Use toSignal to convert the observable to a signal.
  // orders = toSignal(this.orders$, { initialValue: [] });
  displayedColumns = [
    'internalOrderNumber',
    'tracking',
    'receipt',
    'eventDate',
    'orderNumber',
    'customer',
    'eventDescription',
    'orderType',
    'orderStatus',
    'coat',
    'pant',
    'shirt',
    'tie',
    'vest',
    'shoe',
    'pocket',
    'jewelry',
  ] as const;

  // EXT-6424: see compactBelow.
  readonly compactColumns = [
    'actions',
    'eventDate',
    'orderNumber',
    'customer',
    'eventDescription',
    'orderType',
    'orderStatus',
    'garments',
  ] as const;

  get visibleColumns(): readonly string[] {
    return this.compact ? this.compactColumns : this.displayedColumns;
  }

  orderHasShippingStatus(details: OrderListItemDetail[]): boolean {
    // Returns whether or not order has at least 1 fulfilled item.
    return details.some((detailItem) => detailItem.orderStatus === 'S');
  }

  // isMissingSizes(detail: OrderListItemDetail): boolean {
  //   switch (detail.orderProduct) {
  //     case 'CT':
  //       if(detail.orderOption1 == "" || detail.orderOption2 == ""){
  //         return true;
  //       } else {
  //         return false;
  //       }
  //       break;
  //     case 'PT':
  //       if(detail.orderOption1 == "" || detail.orderOption2 == "" || detail.orderOption3 == ""){
  //         return true;
  //       } else {
  //         return false;
  //       }
  //       break;
  //     case 'SR':
  //       if(detail.orderOption1 == "" || detail.orderOption2 == ""){
  //         return true;
  //       } else {
  //         return false;
  //       }
  //     case 'TI':
  //       if(detail.orderOption1 == ""){
  //         return true;
  //       } else {
  //         return false;
  //       }
  //       break;
  //     case 'VS':
  //       if(detail.orderOption1 == ""){
  //         return true;
  //       } else {
  //         return false;
  //       }
  //       break;
  //     case 'SO':
  //       if(detail.orderOption1 == "" || detail.orderOption2 == ""){
  //         return true;
  //       } else {
  //         return false;
  //       }
  //       break;
  //     case 'MS':
  //       if(detail.orderOption1 == ""){
  //         return true;
  //       } else {
  //         return false;
  //       }
  //       break;
  //     case 'HS':
  //       if(detail.orderOption1 == ""){
  //         return true;
  //       } else {
  //         return false;
  //       }
  //       break;
  //     case 'SU':
  //       if(detail.orderOption1 == ""){
  //         return true;
  //       } else {
  //         return false;
  //       }
  //       break;

  //     default:
  //       return false;

  //   }
  // }

  getOrderStatus(order: OrderListItem): string {
    const { details } = order;
    let incompleteOrder = false;
    let missingSizes = false;

    if (details) {
      details.forEach((detailItem) => {
        // Any missing sizes are Incomplete
        if (detailItem.missingSizes === 'Y' && detailItem.orderStatus !== 'D') {
          missingSizes = true;
        }
        // Any orders with S but event date past today are Not Checked
        if (detailItem.outstanding) {
          incompleteOrder = true;
        }
      });
    }

    if (missingSizes) {
      return 'Incomplete';
    }
    if (incompleteOrder) {
      return 'Not Checked In';
    }
    return order.orderStatus;
  }

  formatDateString(dateString: string): string {
    const date = new Date(dateString.replace(' ', 'T'));

    const datePart = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(date);

    const dayName = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
    }).format(date);

    const timePart = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);

    return `${datePart} ${dayName} ${timePart}`;
  }

  // getOrderStatusIcon = (order: OrderListItem): string => {
  //   let iconType = '';
  //   /**
  //    * Options
  //    *  Incomplete, Not Checked In, Placed, Processing, Shipped
  //    */
  //   const orderStatus = this.getOrderStatus(order);
  //   console.warn(orderStatus);
  //   switch (orderStatus) {
  //     case 'Incomplete':
  //       iconType = 'do_not_disturb_on';
  //       break;
  //     case 'Processing':
  //       iconType = 'history_2';
  //       break;
  //     case 'Not Checked In':
  //       iconType = 'rule';
  //       break;
  //     case 'Placed':
  //       iconType = 'domain_verification';
  //       break;
  //     case 'Shipped':
  //       iconType = 'local_shipping';
  //       break;
  //   }
  //   return iconType;
  // };
}
